import React, { useState } from "react";
import { motion } from "motion/react";
import { 
  MapPin, Search, Briefcase, GraduationCap, TrendingUp, Sparkles, 
  CheckCircle, ArrowRight, Building2, ChevronRight, Copy, Check, 
  Info, Users, Award, FileText, Compass, ExternalLink, Globe,
  AlertTriangle, Activity, Wrench, HelpCircle, ChevronDown, BookOpen, Layers
} from "lucide-react";
import { useSeoMetadata, PageNotFound } from "./PublicPages";
import { SeoBreadcrumbs } from "./seo/SeoBreadcrumbs";
import { 
  SeoSearchActionModule, 
  SeoWhyPageExistsSection, 
  SeoExampleResultsPreview, 
  SeoTrustSignalBlock,
  SampleBusinessCard 
} from "./seo/SeoUniversalPageEngine";
import { 
  getJobsLocationUrl, 
  getHiringLocationUrl, 
  getInternshipsLocationUrl,
  getQualifiedLocationsByState
} from "../utils/locationRegistry";
import { 
  resolveLocationSlug, 
  getLocationSearchDestination 
} from "../utils/locationRoutes";
import {
  getDynamicIntroduction,
  getCategorizedEmployers,
  getHiringActivitySnapshot,
  getOutreachStrategy,
  getDynamicFaqs,
  getNearbyLocationsWithContext,
  getContextualGuides,
  getLocationJsonLdSchema
} from "../utils/locationContentEngine";
import { NICHE_JOB_INTENTS, getNicheIntentBySlug } from "../utils/nicheIntentRegistry";
import { 
  generateTier1StateMetadata, 
  generateTier2CityMetadata, 
  generateTier3NicheMetadata,
  getInternalLinkingBlueprint 
} from "../utils/seoProgrammaticEngine";

export interface LocationPageProps {
  onNavigate: (path: string) => void;
  onLogin: (isSignUp?: boolean) => void;
  variant: "jobs" | "businesses-hiring" | "internships" | "local-jobs" | "local-internships";
  locationSlug: string; // e.g. "peoria-il"
  nicheSlug?: string; // e.g. "part-time"
}

export const LocationPage: React.FC<LocationPageProps> = ({
  onNavigate,
  onLogin,
  variant: rawVariant,
  locationSlug: rawSlug,
  nicheSlug
}) => {
  const [copiedScript, setCopiedScript] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

  // Standardize variant
  const variant: "jobs" | "businesses-hiring" | "internships" = 
    rawVariant === "local-jobs" ? "jobs" : 
    rawVariant === "local-internships" ? "internships" : 
    rawVariant;

  // Resolve location slug via centralized resolver
  const resolved = resolveLocationSlug(rawSlug);

  // If invalid or malformed slug, render real 404 page
  if (resolved.type === "invalid") {
    return <PageNotFound onNavigate={onNavigate} />;
  }

  // If reserved slug passed directly, redirect to location directory
  if (resolved.type === "reserved") {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-6 text-center">
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-400">Redirecting to locations directory...</p>
          <button
            onClick={() => onNavigate("/locations")}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-bold text-xs cursor-pointer"
          >
            Go to Locations Directory
          </button>
        </div>
      </div>
    );
  }

  // If unsupported location:
  if (resolved.type === "unsupported" || !resolved.locationRecord) {
    const displayName = resolved.displayName || "Requested Location";
    const canonicalUrl = `https://getbiznearyou.com/${variant}/${resolved.normalizedSlug}`;

    useSeoMetadata({
      title: `Location Guide Not Available - ${displayName} | BizNearYou`,
      description: `We do not currently publish a dedicated location guide for ${displayName}. Search BizNearYou for local businesses and opportunities in this area.`,
      canonicalUrl,
      robots: "noindex, follow"
    });

    return (
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-100 pb-20 pt-12 px-4"
      >
        <div className="max-w-3xl mx-auto bg-white dark:bg-slate-900 rounded-2xl p-8 border border-slate-200 dark:border-slate-800 text-center space-y-6 shadow-xs">
          <div className="w-14 h-14 bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 rounded-full flex items-center justify-center mx-auto border border-amber-200 dark:border-amber-800/60">
            <AlertTriangle className="w-7 h-7" />
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">
              Location Guide Not Available
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-400 max-w-lg mx-auto leading-relaxed">
              We do not currently publish a dedicated location guide for <span className="font-semibold text-slate-900 dark:text-white">{displayName}</span>. However, you can still search local employers and commercial businesses in this area using our interactive business search tool.
            </p>
          </div>

          <div className="pt-2 flex flex-col sm:flex-row justify-center gap-3">
            <button
              onClick={() => onNavigate("/locations")}
              className="bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-xs px-5 py-3 rounded-lg shadow-xs transition cursor-pointer flex items-center justify-center gap-2"
            >
              <Compass className="w-4 h-4" /> Browse Supported Cities Directory
            </button>
            <button
              onClick={() => onNavigate(getLocationSearchDestination(rawSlug))}
              className="bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 font-bold text-xs px-5 py-3 rounded-lg border border-slate-200 dark:border-slate-700 transition cursor-pointer flex items-center justify-center gap-2"
            >
              <Search className="w-4 h-4" /> Launch Employer Search
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  // Supported published location
  const loc = resolved.locationRecord;
  const cityName = loc.cityName;
  const stateAbbr = loc.stateAbbreviation;
  const stateName = loc.stateName;
  const locationSlug = loc.citySlug;

  // Dynamic Content Engine outputs
  const intro = getDynamicIntroduction(loc, variant);
  const categorizedEmployers = getCategorizedEmployers(loc);
  const hiringSnapshot = getHiringActivitySnapshot(loc);
  const outreach = getOutreachStrategy(loc, variant);
  const faqs = getDynamicFaqs(loc, variant);
  const nearbyContext = getNearbyLocationsWithContext(loc, 6);
  const contextualGuides = getContextualGuides(loc, variant);

  // Check if Niche Intent is requested
  const selectedNiche = nicheSlug ? getNicheIntentBySlug(nicheSlug) : null;

  // Programmatic Titles, H1s & Canonical
  let pageTitle = "";
  let pageH1 = "";
  let pageDesc = "";
  let canonicalPath = "";

  if (selectedNiche) {
    const meta3 = generateTier3NicheMetadata(loc, selectedNiche, variant);
    pageTitle = meta3.title;
    pageH1 = meta3.h1;
    pageDesc = meta3.metaDescription;
    canonicalPath = meta3.canonicalUrl;
  } else {
    const meta2 = generateTier2CityMetadata(loc, variant);
    pageTitle = meta2.title;
    pageH1 = meta2.h1;
    pageDesc = meta2.metaDescription;
    canonicalPath = meta2.canonicalUrl;
  }

  // Hook for SEO Head Tags
  useSeoMetadata({
    title: pageTitle,
    description: pageDesc,
    canonicalUrl: canonicalPath
  });

  // Breadcrumbs
  const breadcrumbItems = React.useMemo(() => {
    const list: { label: string; path: string }[] = [];

    if (variant === "businesses-hiring") {
      list.push({ label: "Businesses Hiring", path: "/businesses-hiring/locations" });
    } else if (variant === "internships") {
      list.push({ label: "Internships", path: "/internships/locations" });
    } else {
      list.push({ label: "Jobs", path: "/jobs/locations" });
    }

    if (loc.stateName) {
      const stSlug = loc.regionSlug || loc.stateName.toLowerCase().replace(/\s+/g, "-");
      list.push({ label: loc.stateName, path: `/${variant}/${stSlug}` });
    }

    list.push({ label: loc.cityName, path: `/${variant}/${locationSlug}` });

    if (selectedNiche) {
      list.push({ label: selectedNiche.shortName, path: `/jobs/${locationSlug}/${selectedNiche.slug}` });
    }

    return list;
  }, [variant, loc.stateName, loc.regionSlug, loc.cityName, locationSlug, selectedNiche]);

  // Structured Data JSON-LD Schemas
  const jsonLdSchemas = getLocationJsonLdSchema(loc, variant, canonicalPath, faqs, breadcrumbItems);

  // Dynamic Location Sample Business Cards for Result Preview
  const locationSampleCards: SampleBusinessCard[] = [
    {
      name: `${cityName} Health & Primary Care Clinic`,
      category: "Healthcare & Medical",
      location: `${cityName}, ${stateAbbr}`,
      signalTier: "high",
      signalBadgeText: "High Signal",
      reason: `Patient intake system updated recently with active medical staff expansion indicator in ${cityName}.`,
      freshness: "Verified 2 days ago",
      verifiability: "Verified Public Web Data"
    },
    {
      name: `${cityName} Family Dental & Orthodontics`,
      category: "Dental & Wellness",
      location: `${cityName}, ${stateAbbr}`,
      signalTier: "possible",
      signalBadgeText: "Possible Signal",
      reason: `New suite registered on commercial corridor in ${cityName} with active office contact.`,
      freshness: "Verified 3 days ago",
      verifiability: "Public Business Registry"
    },
    {
      name: `${cityName} Precision Commercial Services`,
      category: "Trades & Operations",
      location: `${cityName}, ${stateAbbr}`,
      signalTier: "unconfirmed",
      signalBadgeText: "Unconfirmed Signal",
      reason: `35+ new 5-star customer reviews in 60 days indicating local operational growth.`,
      freshness: "Verified 1 week ago",
      verifiability: "Review Stream Index"
    }
  ];

  const handleCopyScript = () => {
    navigator.clipboard.writeText(outreach.customScript);
    setCopiedScript(true);
    setTimeout(() => setCopiedScript(false), 2500);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-100 pb-20 pt-6"
    >
      {/* JSON-LD Schema Scripts */}
      {jsonLdSchemas.map((schema, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}

      <div className="max-w-5xl mx-auto px-4 sm:px-6 space-y-8">
        
        {/* Breadcrumb Navigation */}
        <SeoBreadcrumbs items={breadcrumbItems} onNavigate={onNavigate} />

        {/* Hero Header Card */}
        <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 border border-slate-200 dark:border-slate-800 shadow-xs space-y-6">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 bg-blue-50 dark:bg-blue-950/80 text-blue-700 dark:text-blue-300 text-xs font-bold px-3 py-1 rounded-full border border-blue-200 dark:border-blue-800">
                <MapPin className="w-3.5 h-3.5" /> {cityName}, {stateAbbr}
              </span>

              {/* Variant Badge Tabs */}
              <button
                onClick={() => onNavigate(getJobsLocationUrl(locationSlug))}
                className={`text-xs font-bold px-3 py-1 rounded-full transition cursor-pointer ${
                  variant === "jobs"
                    ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-xs"
                    : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
                }`}
              >
                Jobs Guide
              </button>
              <button
                onClick={() => onNavigate(getHiringLocationUrl(locationSlug))}
                className={`text-xs font-bold px-3 py-1 rounded-full transition cursor-pointer ${
                  variant === "businesses-hiring"
                    ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-xs"
                    : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
                }`}
              >
                Hiring Signals
              </button>
              <button
                onClick={() => onNavigate(getInternshipsLocationUrl(locationSlug))}
                className={`text-xs font-bold px-3 py-1 rounded-full transition cursor-pointer ${
                  variant === "internships"
                    ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-xs"
                    : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
                }`}
              >
                Internships
              </button>
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-black text-slate-900 dark:text-white tracking-tight leading-tight">
              {pageH1}
            </h1>

            <p className="text-sm md:text-base text-slate-600 dark:text-slate-300 leading-relaxed max-w-3xl font-normal">
              {intro.headlineSummary}
            </p>
          </div>

          {/* Quick Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 border-t border-slate-100 dark:border-slate-800 text-xs">
            <div className="bg-slate-50 dark:bg-slate-800/60 p-3 rounded-xl border border-slate-100 dark:border-slate-800">
              <span className="text-slate-500 dark:text-slate-400 block font-medium">Regional Metro</span>
              <span className="font-bold text-slate-900 dark:text-white text-sm">{loc.localHighlights.metroName.split("(")[0]}</span>
            </div>
            <div className="bg-slate-50 dark:bg-slate-800/60 p-3 rounded-xl border border-slate-100 dark:border-slate-800">
              <span className="text-slate-500 dark:text-slate-400 block font-medium">Indexed Employers</span>
              <span className="font-bold text-slate-900 dark:text-white text-sm">{loc.businessCount}+ Businesses</span>
            </div>
            <div className="bg-slate-50 dark:bg-slate-800/60 p-3 rounded-xl border border-slate-100 dark:border-slate-800">
              <span className="text-slate-500 dark:text-slate-400 block font-medium">Coverage Radius</span>
              <span className="font-bold text-slate-900 dark:text-white text-sm">{loc.supportedRadius} Miles</span>
            </div>
            <div className="bg-slate-50 dark:bg-slate-800/60 p-3 rounded-xl border border-slate-100 dark:border-slate-800">
              <span className="text-slate-500 dark:text-slate-400 block font-medium">Data Confidence</span>
              <span className="font-bold text-emerald-600 dark:text-emerald-400 text-sm">{loc.dataCoverageScore}% Verified</span>
            </div>
          </div>

          {/* Primary Action Buttons */}
          <div className="pt-2 flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => onNavigate("/find-jobs")}
              className="bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-xs px-6 py-3 rounded-xl shadow-xs transition cursor-pointer flex items-center justify-center gap-2"
            >
              <Search className="w-4 h-4" /> Search {cityName} Employers
            </button>
            <button
              onClick={() => onLogin(true)}
              className="bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 font-bold text-xs px-6 py-3 rounded-xl border border-slate-200 dark:border-slate-700 transition cursor-pointer flex items-center justify-center gap-2"
            >
              <Sparkles className="w-4 h-4 text-amber-500" /> Track Outreach Leads in CRM
            </button>
          </div>

          {/* Disclaimer Banner */}
          <div className="bg-amber-50/80 dark:bg-amber-950/30 border border-amber-200/80 dark:border-amber-800/50 rounded-xl p-3.5 text-xs text-amber-900 dark:text-amber-200 flex items-start gap-2.5">
            <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="leading-relaxed">
              <span className="font-bold">Hiring Signal Transparency:</span> Listed businesses and signal indicators reflect verified public commercial activity. Presence on BizNearYou indicates operational growth, not a guaranteed formal job post. Always approach owners respectfully.
            </div>
          </div>
        </div>

        {/* 1B. Interactive Search & Action Module */}
        <SeoSearchActionModule
          defaultLocation={`${cityName}, ${stateAbbr}`}
          defaultKeyword=""
          onNavigate={onNavigate}
        />

        {/* 1C. Example Results Preview Cards */}
        <SeoExampleResultsPreview
          cards={locationSampleCards}
          locationName={`${cityName}, ${stateAbbr}`}
          onNavigate={onNavigate}
          onLogin={onLogin}
        />

        {/* 1D. Why This Page Exists Comparison */}
        <SeoWhyPageExistsSection
          locationName={`${cityName}, ${stateAbbr}`}
          contextDescription={`Discover how BizNearYou connects job seekers, recruiters, and sales pros directly with business owners across ${cityName} without passive job board fees.`}
        />

        {/* 1. Dynamic Introduction & Strategic Overview Section */}
        <section className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 border border-slate-200 dark:border-slate-800 space-y-4 shadow-xs">
          <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
            <Compass className="w-5 h-5" />
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Navigating {cityName}'s Unadvertised Job Market
            </h2>
          </div>
          
          <div className="space-y-3 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
            {intro.detailedParagraphs.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>

          <div className="pt-2 flex flex-wrap gap-2">
            {intro.keyHighlightsList.map((highlight, idx) => (
              <span key={idx} className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-500" /> {highlight}
              </span>
            ))}
          </div>
        </section>

        {/* 2. Local Hiring Insights & Activity Snapshot Card */}
        <section className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 border border-slate-200 dark:border-slate-800 space-y-6 shadow-xs">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                {cityName} Hiring Activity Snapshot
              </h2>
            </div>
            <span className="text-xs font-bold px-2.5 py-1 bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/80 rounded-md">
              Live Index Signal Data
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 space-y-1">
              <span className="text-xs text-slate-500 dark:text-slate-400 block font-medium">Businesses Indexed</span>
              <span className="text-2xl font-black text-slate-900 dark:text-white">{hiringSnapshot.businessesIndexed}+</span>
              <span className="text-[11px] text-slate-500 block">Verified regional addresses</span>
            </div>

            <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 space-y-1">
              <span className="text-xs text-slate-500 dark:text-slate-400 block font-medium">Est. Direct Leads</span>
              <span className="text-2xl font-black text-indigo-600 dark:text-indigo-400">
                {hiringSnapshot.estimatedUnpostedOpportunitiesMin}–{hiringSnapshot.estimatedUnpostedOpportunitiesMax}
              </span>
              <span className="text-[11px] text-slate-500 block">Unadvertised positions</span>
            </div>

            <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 space-y-1">
              <span className="text-xs text-slate-500 dark:text-slate-400 block font-medium">Signal Confidence</span>
              <span className="text-2xl font-black text-emerald-600 dark:text-emerald-400">{hiringSnapshot.hiringSignalScore}%</span>
              <span className="text-[11px] text-slate-500 block">Recent activity validation</span>
            </div>

            <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 space-y-1">
              <span className="text-xs text-slate-500 dark:text-slate-400 block font-medium">Response Window</span>
              <span className="text-sm font-bold text-slate-900 dark:text-white pt-1 block">{hiringSnapshot.averageResponseWindow}</span>
              <span className="text-[11px] text-slate-500 block">Direct owner pitch speed</span>
            </div>
          </div>

          <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
            <span className="text-xs font-bold text-slate-800 dark:text-slate-200 block mb-3">Sectors Showing Strongest Hiring Momentum:</span>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {hiringSnapshot.topHiringSectors.map((sector, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/30 rounded-xl border border-slate-200/60 dark:border-slate-800">
                  <span className="text-xs font-bold text-slate-800 dark:text-slate-200">{sector.title}</span>
                  <span className="text-[11px] font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/60 px-2.5 py-0.5 rounded-full border border-blue-200 dark:border-blue-800/60">
                    {sector.growthIndicator}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 3. Local Commercial Landscape & Economy */}
        <section className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 border border-slate-200 dark:border-slate-800 space-y-5 shadow-xs">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Local Commercial Landscape in {cityName}
            </h2>
          </div>
          
          <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
            {loc.localHighlights.localContextText}
          </p>

          {loc.localHighlights.majorInstitutions && loc.localHighlights.majorInstitutions.length > 0 && (
            <div className="pt-2">
              <span className="text-xs font-bold text-slate-800 dark:text-slate-200 block mb-2">Primary Anchor Institutions & Employers:</span>
              <div className="flex flex-wrap gap-2">
                {loc.localHighlights.majorInstitutions.map((inst, i) => (
                  <span key={i} className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-medium px-3 py-1 rounded-lg border border-slate-200 dark:border-slate-700">
                    {inst}
                  </span>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* 4. Categorized Employers Vertical Breakdown */}
        <section className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 border border-slate-200 dark:border-slate-800 space-y-6 shadow-xs">
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Categorized {cityName} Employers & Employer Types
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {categorizedEmployers.map((cat, idx) => (
              <div key={idx} className="bg-slate-50 dark:bg-slate-800/40 p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold">
                    {cat.iconName === "Activity" ? <Activity className="w-4 h-4" /> :
                     cat.iconName === "GraduationCap" ? <GraduationCap className="w-4 h-4" /> :
                     cat.iconName === "Wrench" ? <Wrench className="w-4 h-4" /> :
                     <Briefcase className="w-4 h-4" />}
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-white text-sm">{cat.category}</h3>
                </div>
                
                <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                  {cat.description}
                </p>

                <div className="pt-1 flex flex-wrap gap-1.5">
                  {cat.employers.map((emp, eIdx) => (
                    <span key={eIdx} className="text-[11px] font-semibold bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 px-2.5 py-1 rounded-md border border-slate-200/80 dark:border-slate-700">
                      {emp}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 5. Key Districts & Commercial Corridors */}
        <section className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 border border-slate-200 dark:border-slate-800 space-y-6 shadow-xs">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                Major Commercial Districts in {cityName}
              </h2>
            </div>
            <span className="text-xs text-slate-500 font-medium">
              {loc.localHighlights.majorDistricts.length} Primary Zones
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {loc.localHighlights.majorDistricts.map((d, idx) => (
              <div key={idx} className="bg-slate-50 dark:bg-slate-800/40 p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 space-y-2">
                <h3 className="font-bold text-sm text-slate-900 dark:text-white flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-blue-600 dark:text-blue-400" /> {d.name}
                </h3>
                <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">{d.desc}</p>
                <div className="pt-2 text-[11px] text-indigo-600 dark:text-indigo-400 font-semibold flex items-center gap-1">
                  <span>Strategy: Focus on 10–50 employee offices along this corridor</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 6. Outreach Strategy & Custom Pitch Script */}
        <section className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 border border-slate-200 dark:border-slate-800 space-y-6 shadow-xs">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Direct Outreach Strategy for {cityName}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-slate-50 dark:bg-slate-800/40 p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 space-y-2">
              <span className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider block">Target Profile</span>
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">{outreach.targetProfile}</p>
            </div>

            <div className="bg-slate-50 dark:bg-slate-800/40 p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 space-y-2">
              <span className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider block">Optimal Timing</span>
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">{outreach.bestTiming}</p>
            </div>

            <div className="bg-slate-50 dark:bg-slate-800/40 p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 space-y-2">
              <span className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider block">Why Bypassing HR Works</span>
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">{outreach.whyItWorks}</p>
            </div>
          </div>

          {/* Script Copy Card */}
          <div className="bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-2xl p-6 space-y-4 border border-slate-200 dark:border-slate-800 shadow-xs">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="font-bold text-sm text-slate-900 dark:text-white flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-500 dark:text-amber-400" /> Customized {cityName} Direct Pitch Script
              </h3>
              <button
                onClick={handleCopyScript}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-3.5 py-2 rounded-lg flex items-center gap-1.5 transition cursor-pointer shadow-xs"
              >
                {copiedScript ? <><Check className="w-3.5 h-3.5 text-emerald-300" /> Copied to Clipboard</> : <><Copy className="w-3.5 h-3.5" /> Copy Pitch Script</>}
              </button>
            </div>
            
            <pre className="p-4 bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200 rounded-xl text-xs font-mono whitespace-pre-wrap leading-relaxed overflow-x-auto border border-slate-200 dark:border-slate-800">
              {outreach.customScript}
            </pre>
          </div>
        </section>

        {/* 7. Dynamic City-Specific FAQ Section */}
        <section className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 border border-slate-200 dark:border-slate-800 space-y-6 shadow-xs">
          <div className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Frequently Asked Questions About Job Searching in {cityName}
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, idx) => {
              const isOpen = openFaqIndex === idx;
              return (
                <div key={idx} className="border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden transition">
                  <button
                    onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                    className="w-full p-4 text-left font-bold text-sm text-slate-900 dark:text-white bg-slate-50/50 dark:bg-slate-800/30 hover:bg-slate-100/80 dark:hover:bg-slate-800/60 flex items-center justify-between transition cursor-pointer"
                  >
                    <span>{faq.question}</span>
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-180 text-blue-600" : ""}`} />
                  </button>
                  {isOpen && (
                    <div className="p-4 bg-white dark:bg-slate-900 text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed border-t border-slate-100 dark:border-slate-800">
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* 8. Contextual Nearby Locations with Comparison Details */}
        {nearbyContext.length > 0 && (
          <section className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 border border-slate-200 dark:border-slate-800 space-y-6 shadow-xs">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                Nearby Cities & Regional Opportunity Hubs
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {nearbyContext.map((nLoc) => (
                <div key={nLoc.citySlug} className="p-4 bg-slate-50 dark:bg-slate-800/40 rounded-2xl border border-slate-200/80 dark:border-slate-800 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-sm text-slate-900 dark:text-white">
                      {nLoc.cityName}, {nLoc.stateAbbreviation}
                    </span>
                    <span className="text-[11px] font-semibold text-slate-500 bg-slate-200/60 dark:bg-slate-700/60 px-2 py-0.5 rounded">
                      ~{nLoc.distanceMilesEstimate} Miles Away
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                    {nLoc.strategicReason}
                  </p>
                  <div className="pt-1 flex flex-wrap gap-2 text-xs">
                    <button
                      onClick={() => onNavigate(nLoc.jobsUrl)}
                      className="text-blue-600 dark:text-blue-400 hover:underline font-bold text-xs inline-flex items-center gap-1 cursor-pointer"
                    >
                      Jobs in {nLoc.cityName} <ArrowRight className="w-3 h-3" />
                    </button>
                    <span className="text-slate-300 dark:text-slate-700">•</span>
                    <button
                      onClick={() => onNavigate(nLoc.hiringUrl)}
                      className="text-slate-600 dark:text-slate-300 hover:underline text-xs inline-flex items-center gap-1 cursor-pointer"
                    >
                      Hiring Signals
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 9. Contextual Related Playbooks & Guides */}
        <section className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 border border-slate-200 dark:border-slate-800 space-y-6 shadow-xs">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Actionable Direct Outreach Playbooks for {cityName}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {contextualGuides.map((guide, idx) => (
              <div
                key={idx}
                onClick={() => onNavigate(guide.path)}
                className="p-5 bg-slate-50 dark:bg-slate-800/40 rounded-2xl border border-slate-200/80 dark:border-slate-800 space-y-2 hover:border-blue-500/60 transition cursor-pointer group"
              >
                <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider bg-blue-50 dark:bg-blue-950/60 px-2 py-0.5 rounded border border-blue-200 dark:border-blue-800/60">
                  {guide.category}
                </span>
                <h3 className="font-bold text-sm text-slate-900 dark:text-white group-hover:text-blue-600 transition flex items-center justify-between">
                  <span>{guide.title}</span>
                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:translate-x-0.5 transition-transform" />
                </h3>
                <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                  {guide.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* 9b. Niche Job Intent Silo & Internal Linking Architecture */}
        <section className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 border border-slate-200 dark:border-slate-800 space-y-6 shadow-xs">
          <div className="space-y-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-900/50">
              <Layers className="w-3.5 h-3.5" /> Niche Search Silo
            </span>
            <h2 className="text-xl sm:text-2xl font-extrabold text-slate-950 dark:text-white">
              Explore {cityName}, {stateAbbr} Opportunities by Job Intent
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed max-w-3xl">
              Target specific work schedules, remote positions, and entry-level career paths across verified businesses in {cityName}.
            </p>
          </div>

          {/* Niche Intent Pill Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {NICHE_JOB_INTENTS.map((niche) => {
              const isSelected = selectedNiche?.slug === niche.slug;
              return (
                <button
                  key={niche.slug}
                  onClick={() => onNavigate(`/jobs/${locationSlug}/${niche.slug}`)}
                  className={`p-3 rounded-xl border text-left transition cursor-pointer flex flex-col justify-between space-y-2 min-h-[80px] ${
                    isSelected 
                      ? "bg-blue-600 text-white border-blue-700 shadow-sm" 
                      : "bg-slate-50 dark:bg-slate-800/60 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:border-blue-400 dark:hover:border-blue-500"
                  }`}
                >
                  <span className={`text-[10px] font-bold uppercase tracking-wider ${isSelected ? "text-blue-100" : "text-slate-500 dark:text-slate-400"}`}>
                    {niche.category}
                  </span>
                  <span className="text-xs font-extrabold leading-tight">
                    {niche.name} Jobs
                  </span>
                </button>
              );
            })}
          </div>

          {/* Hierarchical Internal Linking Blueprint */}
          <div className="pt-4 border-t border-slate-200 dark:border-slate-800 space-y-4">
            <h3 className="text-xs font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Regional Silo & Navigation Links
            </h3>
            
            <div className="flex flex-wrap gap-2 text-xs font-semibold">
              <a
                href={`/jobs/${loc.stateName.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={(e) => { e.preventDefault(); onNavigate(`/jobs/${loc.stateName.toLowerCase().replace(/\s+/g, "-")}`); }}
                className="px-3 py-1.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-900/50 rounded-lg hover:bg-indigo-100 transition"
              >
                ← Explore All {loc.stateName} Employers Directory
              </a>

              {getQualifiedLocationsByState(stateAbbr)
                .filter(s => s.citySlug !== locationSlug)
                .slice(0, 6)
                .map(sLoc => (
                  <a
                    key={sLoc.citySlug}
                    href={`/jobs/${sLoc.citySlug}`}
                    onClick={(e) => { e.preventDefault(); onNavigate(`/jobs/${sLoc.citySlug}`); }}
                    className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition"
                  >
                    Jobs in {sLoc.cityName}, {sLoc.stateAbbreviation}
                  </a>
                ))}
            </div>
          </div>
        </section>

        {/* 10. Workspace CTA Footer Banner */}
        <div className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white rounded-3xl p-6 md:p-8 space-y-4 shadow-sm text-center border border-slate-200 dark:border-slate-800">
          <p className="text-[11px] font-black uppercase tracking-wider text-blue-600 dark:text-blue-400">START EXPLORING</p>
          <h2 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white">
            Ready to explore local businesses in {cityName}?
          </h2>
          <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 max-w-xl mx-auto leading-relaxed">
            Search nearby businesses, review available public signals, save promising results, and organize your next steps.
          </p>
          <div className="pt-2 flex flex-col sm:flex-row justify-center gap-3">
            <button
              onClick={() => onNavigate("/find-jobs")}
              className="bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-xs px-6 py-3 rounded-xl shadow-xs transition cursor-pointer min-h-[44px]"
            >
              Search Businesses
            </button>
            <button
              onClick={() => onLogin(true)}
              className="bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-800 dark:text-white border border-slate-300 dark:border-slate-700 font-extrabold text-xs px-6 py-3 rounded-xl shadow-xs transition cursor-pointer min-h-[44px]"
            >
              Create Free Account
            </button>
          </div>
        </div>

      </div>
    </motion.div>
  );
};
